import api from './api';

// Helper function to check authentication
const checkAuth = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required. Please sign in.');
  }
  return token;
};

export const createTransaction = async (transactionData) => {
  try {
    checkAuth(); // Check authentication before making API call
    
    const response = await api.post('/api/data_predator/transactions/', transactionData);
    return response.data;
  } catch (error) {
    console.error('Error creating transaction:', error);
    throw error;
  }
};

export const getTransactionData = async (filters = {}) => {
  try {
    checkAuth(); // Check authentication before making API call
    
    const params = new URLSearchParams();
    
    // Add filters to query parameters
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        // Handle multiple stock names
        if (key === 'stock_name' && Array.isArray(filters[key])) {
          filters[key].forEach(stockName => {
            params.append('stock_name', stockName);
          });
        }
        // Handle multiple account names
        else if (key === 'account_name' && Array.isArray(filters[key])) {
          filters[key].forEach(accountName => {
            params.append('account_name', accountName);
          });
        } else {
          params.append(key, filters[key]);
        }
      }
    });

    const response = await api.get(`/api/data_predator/transactions/?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching transaction data:', error);
    throw error;
  }
};

// Helper function to process transaction data for charts
// Check fill status for a single transaction
export const checkTransactionFillStatus = async (transactionId) => {
  try {
    checkAuth();
    
    const response = await api.post(`/api/data_predator/transactions/${transactionId}/check-fill-status/`);
    return response.data;
  } catch (error) {
    console.error('Error checking transaction fill status:', error);
    throw error;
  }
};

// Bulk check fill status for multiple transactions
export const bulkCheckFillStatus = async (transactionIds) => {
  try {
    checkAuth();
    
    const response = await api.post('/api/data_predator/transactions/bulk-check-fill-status/', {
      transaction_ids: transactionIds
    });
    return response.data;
  } catch (error) {
    console.error('Error bulk checking fill status:', error);
    throw error;
  }
};

export const processTransactionDataForChart = (transactionData) => {
  if (!transactionData || !transactionData.results || !Array.isArray(transactionData.results)) {
    return { categories: [], series: [], monthlyDetails: {} };
  }

  // Group transactions by month
  const monthlyData = {};
  
  transactionData.results.forEach(transaction => {
    // Add safety checks for required fields
    if (!transaction || !transaction.transaction_date || !transaction.price || !transaction.quantity) {
      return; // Skip invalid records
    }

    const date = new Date(transaction.transaction_date);
    if (isNaN(date.getTime())) {
      return; // Skip invalid dates
    }

    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    
    if (!monthlyData[monthKey]) {
      monthlyData[monthKey] = {
        buyAmount: 0,
        sellAmount: 0,
        buyCount: 0,
        sellCount: 0,
        accounts: {},
        stocks: {}
      };
    }
    
    const price = parseFloat(transaction.price) || 0;
    const quantity = parseInt(transaction.quantity) || 0;
    const totalAmount = price * quantity;
    
    if (transaction.transaction_type === 'BUY') {
      monthlyData[monthKey].buyAmount += totalAmount;
      monthlyData[monthKey].buyCount += 1;
    } else if (transaction.transaction_type === 'SELL') {
      monthlyData[monthKey].sellAmount += totalAmount;
      monthlyData[monthKey].sellCount += 1;
    }
    
    // Group by account for detailed view
    const accountName = transaction.account_name || 'Unknown Account';
    if (!monthlyData[monthKey].accounts[accountName]) {
      monthlyData[monthKey].accounts[accountName] = {
        buyAmount: 0,
        sellAmount: 0
      };
    }
    
    if (transaction.transaction_type === 'BUY') {
      monthlyData[monthKey].accounts[accountName].buyAmount += totalAmount;
    } else if (transaction.transaction_type === 'SELL') {
      monthlyData[monthKey].accounts[accountName].sellAmount += totalAmount;
    }
    
    // Group by stock for detailed view
    const stockName = transaction.stock_name || 'Unknown';
    if (!monthlyData[monthKey].stocks[stockName]) {
      monthlyData[monthKey].stocks[stockName] = {
        buyAmount: 0,
        sellAmount: 0
      };
    }
    
    if (transaction.transaction_type === 'BUY') {
      monthlyData[monthKey].stocks[stockName].buyAmount += totalAmount;
    } else if (transaction.transaction_type === 'SELL') {
      monthlyData[monthKey].stocks[stockName].sellAmount += totalAmount;
    }
  });

  // Sort months chronologically
  const sortedMonths = Object.keys(monthlyData).sort();
  
  const categories = sortedMonths.map(month => {
    const [year, monthNum] = month.split('-');
    return `${year}-${monthNum}`;
  });
  
  const buyAmounts = sortedMonths.map(month => monthlyData[month]?.buyAmount || 0);
  const sellAmounts = sortedMonths.map(month => monthlyData[month]?.sellAmount || 0);

  return {
    categories: categories || [],
    series: [
      {
        name: 'Buy Amount',
        data: buyAmounts || [],
        type: 'column'
      },
      {
        name: 'Sell Amount',
        data: sellAmounts || [],
        type: 'column'
      }
    ],
    monthlyDetails: monthlyData
  };
};
